import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import {
  MosyUser,
  Task,
  TaskStatus,
} from '../types';

export interface TaskListFilters {
  campaign?: string;
  brand?: string;
  asset?: number;
  unlinked?: boolean;
  assignee?: 'me' | number;
  created_by?: 'me' | number;
  status?: TaskStatus;
}

const listKey = (f?: TaskListFilters) => ['tasks', f ?? {}] as const;

const listTasks = async (f?: TaskListFilters): Promise<Task[]> => {
  const p = new URLSearchParams();
  if (f?.campaign) p.set('campaign', f.campaign);
  if (f?.brand) p.set('brand', f.brand);
  if (f?.asset !== undefined) p.set('asset', String(f.asset));
  if (f?.unlinked) p.set('unlinked', 'true');
  if (f?.assignee !== undefined) p.set('assignee', String(f.assignee));
  if (f?.created_by !== undefined) p.set('created_by', String(f.created_by));
  if (f?.status) p.set('status', f.status);
  const q = p.toString() ? `?${p.toString()}` : '';
  const res = await axios.get(`api/tasks/${q}`);
  return res.data;
};

export const useTasks = (f?: TaskListFilters) =>
  useQuery({ queryKey: listKey(f), queryFn: () => listTasks(f) });

export interface CreateTaskInput {
  id: string;
  name: string;
  due_date: string;
  status: TaskStatus;
  campaign: string | null;
  asset?: number | null;
  assignees?: number[];
}

export const useCreateTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateTaskInput): Promise<Task> => {
      const res = await axios.post('api/tasks/', payload);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
};

export const usePatchTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Task> }): Promise<Task> => {
      const res = await axios.patch(`api/tasks/${id}/`, patch);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};

export const useDeleteTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`api/tasks/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  });
};

export const useTeam = () =>
  useQuery<MosyUser[]>({
    queryKey: ['team'],
    queryFn: async () => {
      const res = await axios.get('api/team/');
      // Sort alphabetically by display name so the assignee picker isn't
      // always anchored on whoever happened to sign up first.
      return [...res.data].sort((a: MosyUser, b: MosyUser) =>
        (a.display_name || a.username).localeCompare(b.display_name || b.username),
      );
    },
    staleTime: 5 * 60_000,
  });
